import { NgModule, inject, provideAppInitializer } from '@angular/core';
import * as Rx from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { Routes } from '@angular/router';
import { AppService, AppSettingService, AppUtilityService } from './app.service';
import { GoogleAPIService } from './google/google.service';
import { UserInfoService } from './user/user-info.service';
import { MonitorHTTP } from './auth.monitor';
import * as nav from './navigation/navigation.component';
import { NotificationComponentModule } from './notification/notification.component.module';
import { SignupLoginComponentModule } from './user/signupLogin/signup-login.component.module';
import { SwitchAccountComponentModule } from './user/switchAccount/switch-account.component.module';
import { SocialObjectAPIService } from './socialObject/social-object.service';
import { AccountAPIService } from './user/account-api.service';

const journeyRoutes: Routes = [
	{ path: '', component: nav.NavigationHomeComponent },
	{ path: 'Activity', loadChildren: () => import('./activity/activity.module').then(m => m.ActivityModule) },
	{ path: 'Career', loadChildren: () => import('./career/career.module').then(m => m.CareerModule) },
	{ path: 'Certification', loadChildren: () => import('./certification/certification.module').then(m => m.CertificationModule) },
	{ path: 'College', loadChildren: () => import('./college/college.module').then(m => m.CollegeModule) },
	{ path: 'Class', loadChildren: () => import('./class/class.module').then(m => m.ClassModule) },
	{ path: 'CollegeScorecard', loadChildren: () => import('./collegeScorecard/college-scorecard.module').then(m => m.CollegeScorecardModule) },
	{ path: 'Comment', loadChildren: () => import('./comment/comment.module').then(m => m.CommentModule) },
	{ path: 'CTE', loadChildren: () => import('./cte/cte.module').then(m => m.CTEModule) },
	{ path: 'Cyber', loadChildren: () => import('./cyber/cyber.module').then(m => m.CyberModule) },
	{ path: 'Error', component: nav.NavigationErrorComponent, data: { authenticate: false, routeName: "Error" } },
	{ path: 'Error/SSO', component: nav.NavigationErrorComponent, data: { authenticate: false, routeName: "SSO Error" } },
	{ path: 'Error/Permission', component: nav.NavigationErrorComponent, data: { authenticate: false, routeName: "Permission Error" } },
	{ path: 'Error/STokenExp', component: nav.NavigationErrorComponent, data: { authenticate: false, routeName: "SSO Expiration" } },
	{ path: 'InterestSurvey', loadChildren: () => import('./interestSurvey/interest-survey.module').then(m => m.InterestSurveyModule) },
	{ path: 'JaStudentDashboard', loadChildren: () => import('./ja/studentDashboard/student-dashboard.module').then(m => m.JaStudentDashboardModule) },
	{ path: 'Job', loadChildren: () => import('./job/job.module').then(m => m.JobModule) },
	{ path: 'K12', loadChildren: () => import('./k12/k12.module').then(m => m.K12Module) },
	{ path: 'Map', loadChildren: () => import('./map/map.module').then(m => m.MapModule) },
	{ path: 'Military', loadChildren: () => import('./military/military.module').then(m => m.MilitaryModule) },
	{ path: 'MyFollowing', loadChildren: () => import('./socialObject/myFollowing/my-following.module').then(m => m.MyFollowingModule) },
	{ path: 'MyJourney', loadChildren: () => import('./myJourney/my-journey.module').then(m => m.MyJourneyModule) },
	{ path: 'NCES', loadChildren: () => import('./nces/nces.module').then(m => m.NCESModule) },
	{ path: "ONET", loadChildren: () => import('./onet/onet.module').then(m => m.ONETModule) },
	{ path: 'PrivacyPolicy', loadChildren: () => import('./termPolicy/term-policy.module').then(m => m.TermPolicyModule) },
	{ path: 'School', loadChildren: () => import('./school/school.module').then(m => m.SchoolModule) },
	{ path: 'Search', loadChildren: () => import('./search/search.module').then(m => m.SearchModule) },
	{ path: 'SkillProfiler', loadChildren: () => import('./skillProfiler/skill-profiler.module').then(m => m.SkillProfilerModule) },
	{ path: "Splash", component: nav.NavigationSplashComponent },
	{ path: 'TermsOfService', loadChildren: () => import('./termPolicy/term-policy.module').then(m => m.TermPolicyModule) },
	{ path: 'TerritoryCard', loadChildren: () => import('./territoryCard/territory-card.module').then(m => m.TerritoryCardModule) },
	{ path: 'User', loadChildren: () => import('./user/user.module').then(m => m.UserModule) },
	{ path: 'Zone', loadChildren: () => import('./zone/zone.module').then(m => m.ZoneModule) },
	{ path: 'FileManagement', loadChildren: () => import('./fileManagement/file-management.module').then(m => m.FileManagementModule) },
	{ path: 'Landing', loadChildren: () => import('./zoneLanding/zone-landing.module').then(m => m.ZoneLandingModule) },
	{ path: 'MatrixMap', loadChildren: () => import('./matrixMap/matrix-map.module').then(m => m.MatrixMapModule) },
	{ path: "ManageWidgets", loadChildren: () => import('./manageWidgets/manage-widgets.component.module').then(m => m.ManageWidgetsComponentModule) },
	{ path: 'System', loadChildren: () => import('./system/system.module').then(m => m.SystemModule) },
	{ path: "DNAResponse", loadChildren: () => import('./dnaResponse/dna-response.module').then(m => m.DNAResponseModule) },
	{ path: "Preview", loadChildren: () => import('./preview/preview.module').then(m => m.PreviewModule) },
	{ path: '**', component: nav.NavigationErrorComponent }
];

@NgModule({
	declarations: [
		AppComponent,
		nav.NavigationHeaderComponent,
		nav.NavigationFooterComponent,
		nav.NavigationIdleComponent,
		nav.NavigationIdleWarningComponent
	],
	imports: [
		CommonModule,
		BrowserModule,
		BrowserAnimationsModule,
		FormsModule,
		ReactiveFormsModule,
		NgbModule,
		MatDialogModule,
		NotificationComponentModule,
		SignupLoginComponentModule,
		SwitchAccountComponentModule,
		NgIdleKeepaliveModule.forRoot(),
		RouterModule.forRoot(journeyRoutes)
	],
	providers: [
		AppSettingService,
		AppUtilityService,
		UserInfoService,
		provideAppInitializer(() => {
			const googleApi = inject(GoogleAPIService);
			return Rx.firstValueFrom(
				inject(HttpClient).get<any>(AppSettingService.appConfig.JourneyAPIUrl + "Config/JourneysMap")
					.pipe(tap((config: any) => {
						AppSettingService.appConfig = config;
						googleApi.loadScripts();
					}))
			).catch(() => {
				AppSettingService.appConfig = null;
				alert("Fail to load configuration, please reload this page");
				return;
			});
		}),
		provideAppInitializer(() => {
			inject(AppService).checkAccountCookie();
		}),
		provideAppInitializer(() => {
			inject(AccountAPIService).initMyProfile();
		}),
		provideAppInitializer(() => {
			inject(SocialObjectAPIService).initUserSocialObjects();
		}),
		{
			provide: HTTP_INTERCEPTORS,
			useClass: MonitorHTTP,
			multi: true
		},
		{
			provide: MatDialogRef,
			useValue: {}
		},
		provideHttpClient(withInterceptorsFromDi())
	],
	bootstrap: [AppComponent]
})
export class AppModule { }
